import { useCallback, useEffect, useRef } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { DateTimeValueLabel } from '@/lib/crm/components/properties/labels/DateTimeValueLabel';
import { DateValueLabel } from '@/lib/crm/components/properties/labels/DateValueLabel';
import { PropertyInput } from '@/lib/crm/components/properties/PropertyInput';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import { InlineFieldError } from '../components/InlineFieldError';
import { NOOP_SAVE, useInlineFieldEdit } from './useInlineFieldEdit';

export interface TaskDueDateFieldProps {
  date?: string;
  datetime?: string;
  dueDateSchema?: PropertyTypeSchema;
  onSave?: (newDate: string | null) => Promise<void>;
}

export function TaskDueDateField({
  date,
  datetime,
  dueDateSchema,
  onSave,
}: TaskDueDateFieldProps) {
  const { isEditing, error, startEditing, cancelEditing, commit } =
    useInlineFieldEdit(onSave ?? NOOP_SAVE);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasEditingRef = useRef(false);
  // Set the instant a date is picked (which itself closes the popover). The
  // picker's dismissal `onOpenChange(false)` must NOT be treated as a cancel in
  // that case — the pending commit owns the editor's lifecycle. A genuine
  // dismissal (Escape / click-away with nothing picked) leaves this false.
  const committedRef = useRef(false);

  const editable = !!onSave && !!dueDateSchema;

  // The inline editor is date-only, but a task may carry only a datetime (the
  // backend cascade normally also derives ahoy_date). Seed the picker from the
  // date when present, else from the datetime's date part, so opening Due date
  // never starts on an empty calendar when a due value exists.
  const pickerValue = date ?? (datetime ? datetime.split('T')[0] : null);

  // Return focus to the trigger when the editor closes (on a completed pick,
  // an escape, or a dismissal). The trigger is unmounted while the editor is
  // open, so focus can only be restored once it re-mounts — from an effect,
  // not inline in the key handler where the ref is still null.
  useEffect(() => {
    if (wasEditingRef.current && !isEditing) {
      triggerRef.current?.focus();
    }
    wasEditingRef.current = isEditing;
  }, [isEditing]);

  const handlePicked = useCallback(
    (v: unknown) => {
      committedRef.current = true;
      void commit((v as string | null) ?? null);
    },
    [commit]
  );

  // The picker closing is the canonical dismissal signal (the wrapper's own
  // key handler never sees the popover's Escape — react-aria consumes it). Turn
  // a close with nothing picked into a cancel so the wrapper doesn't stay stuck
  // in edit mode; a close caused by a pick is owned by the pending commit.
  const handlePickerOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        // Opening the picker begins a fresh selection cycle. Clear any stale
        // "committed" flag left set by a prior pick whose save then failed —
        // otherwise a later dismissal (Escape / click-away with nothing
        // picked) on the reopened picker would be misread as a completed pick
        // and fail to cancel, stranding the editor open.
        committedRef.current = false;
        return;
      }
      if (!committedRef.current) {
        cancelEditing();
      }
    },
    [cancelEditing]
  );

  const handleKeyDown = useCallback(
    (e: { key: string }) => {
      if (e.key === 'Escape') {
        cancelEditing();
      }
    },
    [cancelEditing]
  );

  const handleStartEditing = useCallback(() => {
    committedRef.current = false;
    startEditing();
  }, [startEditing]);

  // Default `sm` (14px) deliberately matches OverviewFieldRow's label size
  // (`text-xs md:text-sm`) — the same value/label pairing the canonical overview
  // row uses (overview-section.tsx). An `xs` override here rendered the value
  // column smaller than its own label and off its optical line.
  const readValue = datetime ? (
    <DateTimeValueLabel value={datetime} />
  ) : (
    <DateValueLabel value={date ?? null} />
  );

  if (isEditing && dueDateSchema) {
    return (
      // Negative vertical margins cancel the `xs` field-control's extra chrome
      // (32px desktop box vs the 24px read row), so entering edit shifts
      // nothing below — the control's box bleeds into the row gaps instead of
      // growing the row. Same zero-shift approach as TaskTitleField's
      // EDIT_WRAPPER_CLASS.
      <div className='-my-1' onKeyDown={handleKeyDown}>
        <PropertyInput
          propertyType={dueDateSchema}
          value={pickerValue}
          autoOpenPicker
          size='xs'
          onChange={handlePicked}
          onPickerOpenChange={handlePickerOpenChange}
        />
        <InlineFieldError message={error} />
      </div>
    );
  }

  if (editable) {
    return (
      <div>
        <AriaButton
          ref={triggerRef}
          aria-label='Edit due date'
          onPress={handleStartEditing}
          className='outline-focus-ring rounded'
        >
          {readValue}
        </AriaButton>
        <InlineFieldError message={error} />
      </div>
    );
  }

  return readValue;
}
